import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiErr, get, type Env } from './api';
import { reportClientError } from './sentry';

export interface Fetched<T> {
  data: T | null;
  error: ApiErr | null;
  loading: boolean;
  at: number | null;
  ageS: number;
  reload: () => void;
}

export function useData<T>(env: Env, path: string | null, everyS: number, paused: boolean, deps: unknown[] = []): Fetched<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiErr | null>(null);
  const [loading, setLoading] = useState(!!path);
  const [at, setAt] = useState<number | null>(null);
  const [tick, setTick] = useState(0);
  const gen = useRef(0);
  const load = useCallback(async () => {
    if (!path) return;
    const g = ++gen.current;
    try {
      const d = await get<T>(env, path);
      if (g !== gen.current) return;
      setData(d);
      setError(null);
      setAt(Date.now());
    } catch (e) {
      if (g !== gen.current) return;
      reportClientError(e);
      setError(e as ApiErr);
    } finally {
      if (g === gen.current) setLoading(false);
    }
  }, [env, path]);
  useEffect(() => {
    setLoading(!!path);
    setData(null);
    setError(null);
    setAt(null);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, ...deps]);
  useEffect(() => {
    if (!path || everyS <= 0 || paused) return;
    const id = setInterval(() => { if (document.visibilityState === 'visible') void load(); }, everyS * 1000);
    return () => clearInterval(id);
  }, [path, everyS, paused, load]);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  void tick;
  return { data, error, loading, at, ageS: at ? Math.floor((Date.now() - at) / 1000) : 0, reload: () => void load() };
}

export function useMobile(): boolean {
  const [m, setM] = useState(() => window.innerWidth < 760);
  useEffect(() => {
    const on = () => setM(window.innerWidth < 760);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return m;
}

export function describeErr(e: ApiErr | null, what = 'The API'): { title: string; text?: string } | null {
  if (!e) return null;
  if (e.status === 0) return { title: `${what} did not answer`, text: e.message };
  if (e.status === 403) return { title: 'Not allowed', text: e.message };
  if (e.code === 'engine_unavailable') return { title: e.message.split(':')[0] || 'Upstream failed', text: e.message };
  return { title: `${what} answered ${e.status}`, text: e.message };
}

export function hhmmss(ms: number | null): string {
  return ms ? new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';
}
